import React, { useState } from 'react';
import { useQRCodePDF } from '../../../../services/useQRCodePDF';
import { useToast } from '../../../../components/ui/Toast';
import POSSessionCard from '../../../../components/ui/POSSessionCard';
import QRModal from '../../../../components/ui/QRModal';
import { useI18n } from '../../../../i18n/I18n';

interface Step3Props {
    session: any;
    onClose: () => void;
}

export default function Step3Success({ session, onClose }: Step3Props) {
    const { t } = useI18n();
    const { downloadQRCodePDF } = useQRCodePDF();
    const toast = useToast();
    const [isQrModalOpen, setIsQrModalOpen] = useState(false);

    const sessionId = session?.sessionId;

    const handleCopyLink = () => {
        if (!sessionId) return;
        const link = `https://pay.sprintcheckout.com/?uid=${sessionId}`;
        navigator.clipboard.writeText(link);
        toast({ title: t('pos.linkCopied'), status: "success", duration: 2000, bottomOffset: '6rem' });
    };

    const handleDownloadPDF = async () => {
        try {
            if (!sessionId) return;
            const link = `https://pay.sprintcheckout.com/?uid=${sessionId}`;
            await downloadQRCodePDF(link, { sessionName: session?.name || session?.concept || '' });
        } catch (err) {
            console.error('Error downloading QR PDF:', err);
        }
    };

    const handleWelcomeCard = async () => {
        try {
            if (!sessionId) return;
            await downloadQRCodePDF(
                `https://dashboard.sprintcheckout.com/#/auth/sign-in/default?join=${sessionId}`,
                {
                    basePdf: 'spcWelcomeCard',
                    filename: 'Sprintcheckout-welcome-card.pdf',
                    position: { x: 260, y: 565, width: 80, height: 80 },
                }
            );
        } catch (err) {
            console.error('Error downloading welcome card:', err);
        }
    };

    return (
        <div className="flex-1 flex flex-col min-h-0 h-full w-full animate-in fade-in zoom-in duration-500 text-center">

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto w-full no-scrollbar">
                <div className="flex flex-col items-center justify-center pt-4 pb-6 px-6">
                    <div className="w-20 h-20 rounded-full bg-green-500/20 text-green-500 flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(34,197,94,0.3)]">
                        <span className="material-symbols-outlined text-[40px]">check</span>
                    </div>

                    <h2 className="text-2xl font-bold text-txt-heading mb-2">{t('wizard.posCreated')}</h2>
                    <p className="text-txt-secondary text-sm mb-10 max-w-xs mx-auto">{t('wizard.posReady')}</p>

                    <div className="w-full max-w-sm mx-auto mb-6 text-left">
                        {session && (
                            <POSSessionCard
                                session={session}
                                isExpanded={false}
                                hideChevron={true}
                                onOpenQrModal={() => setIsQrModalOpen(true)}
                                onCopyLink={handleCopyLink}
                                onDownloadPDF={handleDownloadPDF}
                                onWelcomeCard={handleWelcomeCard}
                                shouldShowWelcomeCard={true}
                            />
                        )}
                    </div>

                    {session && (
                        <div className="w-full max-w-sm mx-auto mb-10 glass-panel rounded-2xl p-4" style={{ borderColor: 'var(--color-surface-border)' }}>
                            <div className="grid grid-cols-3 gap-2">
                                <button
                                    onClick={handleCopyLink}
                                    className="group/btn flex flex-col items-center bg-transparent border-none cursor-pointer"
                                >
                                    <div className="w-11 h-11 rounded-full flex items-center justify-center mb-1.5 transition-colors group-hover/btn:bg-electric-blue/20 group-hover/btn:text-electric-blue text-txt-secondary" style={{ background: 'var(--color-surface)' }}>
                                        <span className="material-symbols-outlined text-[20px]">link</span>
                                    </div>
                                    <span className="text-[9px] font-semibold text-txt-muted uppercase tracking-tighter">{t('pos.paymentLink')}</span>
                                </button>
                                <button
                                    onClick={handleDownloadPDF}
                                    className="group/btn flex flex-col items-center bg-transparent border-none cursor-pointer"
                                >
                                    <div className="w-11 h-11 rounded-full flex items-center justify-center mb-1.5 transition-colors group-hover/btn:bg-electric-blue/20 group-hover/btn:text-electric-blue text-txt-secondary" style={{ background: 'var(--color-surface)' }}>
                                        <span className="material-symbols-outlined text-[20px]">picture_as_pdf</span>
                                    </div>
                                    <span className="text-[9px] font-semibold text-txt-muted uppercase tracking-tighter">{t('pos.downloadPdf')}</span>
                                </button>
                                <button
                                    onClick={handleWelcomeCard}
                                    className="group/btn flex flex-col items-center bg-transparent border-none cursor-pointer"
                                >
                                    <div className="w-11 h-11 rounded-full flex items-center justify-center mb-1.5 transition-colors group-hover/btn:bg-electric-blue/20 group-hover/btn:text-electric-blue text-txt-secondary" style={{ background: 'var(--color-surface)' }}>
                                        <span className="material-symbols-outlined text-[20px]">badge</span>
                                    </div>
                                    <span className="text-[9px] font-semibold text-txt-muted uppercase tracking-tighter">{t('pos.welcomeCard')}</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal for QR Code */}
                {isQrModalOpen && session && (
                    <QRModal
                        onClose={() => setIsQrModalOpen(false)}
                        session={session}
                        onCopyLink={handleCopyLink}
                        onDownloadPDF={handleDownloadPDF}
                    />
                )}

            </div>

            {/* Main Action (pinned to bottom) */}
            <div className="pt-4 pb-6 px-6 w-full flex backdrop-blur-xl flex-shrink-0" style={{ background: 'var(--sticky-bg)' }}>
                <button
                    onClick={onClose}
                    className="w-full bg-electric-blue hover:bg-electric-blue/90 text-white font-bold py-4 rounded-xl transition-all shadow-lg active:scale-95"
                >
                    {t('wizard.back')}
                </button>
            </div>
        </div>
    );
}
